import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Alert,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatKstDateTime, formatRelative } from '../../utils/formatUtils';
import { PageHeader, useConfirm, useNotify } from '../../components/common';

const Agents = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [confirmDialog, ask] = useConfirm();
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState({ agents: [] });
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/agents', { params: { status: status === 'all' ? '' : status, search: debounced } });
      setData(res.data);
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, [status, debounced]);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = (a) =>
    ask({
      title: '에이전트 폐기',
      message: `${a.username || a.userId} 회원의 '${a.name || a.agentId}' 에이전트 인증을 폐기합니다.\n그 PC는 중앙과 동기화·알림 전송을 할 수 없게 됩니다.`,
      confirmText: '폐기',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await apiClient.post(`/admin/agents/${a.agentId}/revoke`, { userId: a.userId });
          notify(res.data.message);
          load();
        } catch (e) {
          notify(getErrorMessage(e), 'error');
        }
      },
    });

  return (
    <>
      <PageHeader
        title="에이전트"
        description={`회원 서버에 설치된 PlanGo Agent 목록. 최근 ${data.agentOnlineMinutes ?? '-'}분 안에 동기화했으면 '접속중'으로 표시합니다.`}
        actions={<Button startIcon={<RefreshIcon />} onClick={load}>새로고침</Button>}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={status} onChange={(_e, v) => v && setStatus(v)}>
            <ToggleButton value="active">사용중</ToggleButton>
            <ToggleButton value="online">접속중</ToggleButton>
            <ToggleButton value="offline">오프라인</ToggleButton>
            <ToggleButton value="revoked">폐기됨</ToggleButton>
            <ToggleButton value="all">전체</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder="회원 · PC 이름 · 에이전트 ID · IP"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </Stack>
      </Paper>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>회원</TableCell>
              <TableCell>에이전트 ID</TableCell>
              <TableCell>PC 이름</TableCell>
              <TableCell>버전</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>마지막 동기화</TableCell>
              <TableCell>IP (등록 / 최근)</TableCell>
              <TableCell>등록일</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.agents.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  에이전트가 없습니다.
                </TableCell>
              </TableRow>
            )}
            {data.agents.map((a) => (
              <TableRow key={a.agentId} hover>
                <TableCell sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/users/${encodeURIComponent(a.userId)}`)}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.username || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">{a.email} · #{a.userId}</Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{a.agentId}</TableCell>
                <TableCell>{a.name || '-'}</TableCell>
                <TableCell>{a.version || '-'}</TableCell>
                <TableCell>
                  {a.revokedAt ? (
                    <Tooltip title={`${formatKstDateTime(a.revokedAt)} · ${a.revokedReason || ''}`}>
                      <Chip size="small" label="폐기됨" variant="outlined" />
                    </Tooltip>
                  ) : (
                    <Chip size="small" label={a.online ? '접속중' : '오프라인'} color={a.online ? 'success' : 'default'} variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={formatKstDateTime(a.lastSeenAt)}>
                    <span>{formatRelative(a.lastSeenAt)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{a.registeredIp || '-'} / {a.lastIp || '-'}</TableCell>
                <TableCell>{formatKstDateTime(a.createdAt, { seconds: false })}</TableCell>
                <TableCell align="right">
                  {!a.revokedAt && (
                    <Button size="small" color="error" onClick={() => revoke(a)}>폐기</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {confirmDialog}
    </>
  );
};

export default Agents;
